import React, { useState, useEffect } from "react";
import { X, Save, BookOpen, FileText } from "lucide-react";
import toast from "react-hot-toast";
import {
  StationeryItem,
  CreateStationeryPayload,
  createStationery,
  updateStationery,
} from "../../services/admin";

interface StationeryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: StationeryItem | null;
}

export const StationeryModal: React.FC<StationeryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [unitPrice, setUnitPrice] = useState<string | number>("");
  const [isActive, setIsActive] = useState<boolean>(true);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name || "");
      setDescription(initialData.description || "");
      setUnitPrice(initialData.unit_price !== undefined ? initialData.unit_price : "");
      setIsActive(initialData.is_active !== undefined ? initialData.is_active : true);
    } else {
      setName("");
      setDescription("");
      setUnitPrice("");
      setIsActive(true);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const formatNumberWithCommas = (val: string | number) => {
    if (val === "" || val === undefined || val === null) return "";
    const raw = String(val).replace(/\D/g, "");
    if (!raw) return "";
    return Number(raw).toLocaleString("en-US");
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    setUnitPrice(raw === "" ? "" : Number(raw));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Please enter a stationery name.");
      return;
    }
    if (unitPrice === "" || Number(unitPrice) < 0) {
      toast.error("Please enter a valid unit price.");
      return;
    }

    setSubmitting(true);
    try {
      const payload: CreateStationeryPayload = {
        name: name.trim(),
        description: description.trim(),
        unit_price: Number(unitPrice),
        is_active: isActive,
      };

      if (initialData?.id) {
        await updateStationery({ id: initialData.id, payload });
        toast.success("Stationery updated successfully!");
      } else {
        await createStationery(payload);
        toast.success("Stationery created successfully!");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Failed to save stationery:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save stationery item. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-hidden animate-in fade-in duration-300">
      <div className="bg-white rounded-[2.5rem] w-full max-w-xl max-h-[88vh] p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col relative animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center border border-blue-100">
              <BookOpen size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-800">
                {initialData ? "Edit Stationery" : "Add New Stationery"}
              </h3>
              <p className="text-slate-400 font-bold text-xs">
                {initialData
                  ? "Update stationery item details and pricing."
                  : "Create a new stationery item for invoicing."}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto py-6 px-2 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              Stationery Name <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <BookOpen className="absolute left-4 top-3.5 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="e.g. Text Books, Uniform, Art Supplies"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-semibold text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition text-sm"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              Description
            </label>
            <div className="relative">
              <FileText className="absolute left-4 top-3.5 text-slate-400" size={18} />
              <textarea
                rows={3}
                placeholder="e.g. Main school books required for term 1"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-semibold text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition text-sm resize-none"
              />
            </div>
          </div>

          {/* Unit Price */}
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">
              Unit Price (₦) <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute left-4 top-3.5 text-slate-500 font-black text-sm">₦</div>
              <input
                type="text"
                placeholder="0"
                value={formatNumberWithCommas(unitPrice)}
                onChange={handlePriceChange}
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl font-black text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition text-base"
                required
              />
            </div>
          </div>

          {/* Is Active Status */}
          <div className="pt-2 flex items-center justify-between bg-slate-50 p-4 rounded-2xl border border-slate-100">
            <div>
              <p className="text-sm font-black text-slate-800">Active Status</p>
              <p className="text-xs text-slate-400 font-semibold">
                Available for student invoice selection
              </p>
            </div>

            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </form>

        {/* Modal Footer Buttons */}
        <div className="pt-4 mt-2 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-3 text-slate-600 hover:bg-slate-100 rounded-2xl font-black text-sm transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-black text-sm rounded-2xl transition flex items-center gap-2 shadow-lg shadow-blue-200 disabled:opacity-50"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save size={18} />
                {initialData ? "Update Stationery" : "Create Stationery"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
